"use client";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";


import { ChatContactListMessages } from "@/messages/chat/contact-list.messages";
import {
  AIAgentsFilter,
  ConversationFilter,
  ConversationView,
  MyConversationsFilter,
} from "@/types/conversation-filters.types";

type Props = {
  view: ConversationView;
  filter: ConversationFilter;
  onFilterChange: (filter: ConversationFilter) => void;
  messages: ChatContactListMessages;
  counts?: Partial<Record<ConversationFilter, number>>;
};

function ConversationFilters({ view, filter, onFilterChange, messages, counts }: Props) {
  const optionsByView: Record<ConversationView, { value: ConversationFilter; label: string }[]> = {
    [ConversationView.MY_CONVERSATIONS]: [
      { value: MyConversationsFilter.UNREAD, label: messages.unreadChats },
      { value: MyConversationsFilter.OPEN, label: messages.openChats },
      { value: MyConversationsFilter.AWAITING_CLIENT, label: messages.awaitingClientChats },
      { value: MyConversationsFilter.CLOSED, label: messages.closedChats },
    ],
    [ConversationView.AI_AGENTS]: [
      { value: AIAgentsFilter.ATTENDING, label: messages.attendingChats },
      { value: AIAgentsFilter.CLOSED, label: messages.closedChats },
    ],
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateArrows, view, counts]);

  const scrollBy = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 140, behavior: "smooth" });
  };

  const arrowSx = (side: "left" | "right") => ({
    position: "absolute" as const,
    top: "50%",
    transform: "translateY(-50%)",
    [side]: 0,
    zIndex: 2,
    width: 22,
    height: 22,
    backgroundColor: "background.paper",
    boxShadow: 2,
    "&:hover": { backgroundColor: "background.paper" },
  });

  return (
    <Box sx={{ position: "relative" }}>
      {canScrollLeft && (
        <IconButton size="small" aria-label="anterior" onClick={() => scrollBy(-1)} sx={arrowSx("left")}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton size="small" aria-label="próximo" onClick={() => scrollBy(1)} sx={arrowSx("right")}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        ref={scrollRef}
        onScroll={updateArrows}
        sx={{
          overflowX: "auto",
          pb: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
      <ToggleButtonGroup
        value={filter}
        exclusive
        onChange={(_, newFilter: ConversationFilter | null) => {
          if (newFilter !== null) onFilterChange(newFilter);
        }}
        size="small"
        sx={{
          gap: 0.75,
          flexWrap: "nowrap",
          "& .MuiToggleButtonGroup-grouped": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "0.6rem !important",
            textTransform: "none",
            whiteSpace: "nowrap",
            fontSize: "0.7rem",
            fontWeight: 600,
            px: 1.25,
            py: 0.5,
            gap: 0.6,
            color: "text.secondary",
            "&.Mui-selected": {
              borderColor: "primary.main",
              color: "primary.main",
              backgroundColor: "transparent",
            },
            "&.Mui-selected:hover": { backgroundColor: "transparent" },
          },
        }}
      >
        {optionsByView[view].map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
            {typeof counts?.[option.value] === "number" && (
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: filter === option.value ? "primary.main" : "text.primary",
                }}
              >
                {counts[option.value]}
              </Box>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

export default ConversationFilters;
